import React, { useState, useEffect, useMemo, useRef } from "react";
import CustomModal from "./customModal";
import FormInput from "../Input/formInput";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../toast/toast";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../../../pages/staff/dropdownStyles";

const CHANGE_OPTIONS = [
  { value: "increase", label: "Increase Stock" },
  { value: "decrease", label: "Decrease Stock" },
];

const AdjustStockModal = ({ isOpen, onClose, onSuccess, menuItemId, compID, itemName }) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [loading, setLoading] = useState(false);
  const [allUnits, setAllUnits] = useState([]);
  const [itemDetails, setItemDetails] = useState(null);
  const [batches, setBatches] = useState([]);
  const defaultUnitSetRef = useRef(false);
  const [showCostPriceHelp, setShowCostPriceHelp] = useState(false);
  const [costPriceHelpPinned, setCostPriceHelpPinned] = useState(false);
  const costPriceHelpRef = useRef(null);
  const [form, setForm] = useState({
    changeType: "increase",
    quantity: "",
    unit: "Piece",
    costPrice: "",
    batchNumber: "",
    expiryDate: "",
    notes: "",
    deductionMode: "fifo",
    inventoryStockId: "",
  });

  const measurementType = itemDetails?.menuItem_MeasurementType ?? itemDetails?.MenuItem_MeasurementType ?? "";
  const defaultUnitFromItem = itemDetails?.menuItem_DefaultSizeUnit ?? itemDetails?.MenuItem_DefaultSizeUnit ?? "";

  const unitOptions = useMemo(() => {
    if (!Array.isArray(allUnits) || allUnits.length === 0) {
      return [{ value: "Piece", label: "Piece" }];
    }
    const filtered =
      measurementType
        ? allUnits.filter(
            (u) => (u.MeasurementTypeName ?? u.measurementTypeName ?? "") === measurementType
          )
        : allUnits;
    const opts = filtered
      .map((u) => {
        const value = u.MeasureName ?? u.measureName ?? u.unitName ?? u.name ?? "";
        const label = u.symbol ?? u.Symbol ?? value;
        return value ? { value, label } : null;
      })
      .filter(Boolean);
    if (opts.length === 0) return [{ value: "Piece", label: "Piece" }];
    return opts;
  }, [allUnits, measurementType]);

  useEffect(() => {
    if (isOpen) {
      defaultUnitSetRef.current = false;
      setShowCostPriceHelp(false);
      setCostPriceHelpPinned(false);
      setForm((prev) => ({
        ...prev,
        changeType: "increase",
        quantity: "",
        unit: "Piece",
        costPrice: "",
        batchNumber: "",
        expiryDate: "",
        notes: "",
        deductionMode: "fifo",
        inventoryStockId: "",
      }));
      setItemDetails(null);
      fetchUnits();
      if (menuItemId && compID) {
        fetchItemDetails();
        fetchBatches();
      }
    }
  }, [isOpen, menuItemId, compID]);

  // Close pinned help when clicking outside
  useEffect(() => {
    if (!costPriceHelpPinned) return;
    const handleOutsideClick = (e) => {
      if (costPriceHelpRef.current && !costPriceHelpRef.current.contains(e.target)) {
        setCostPriceHelpPinned(false);
        setShowCostPriceHelp(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [costPriceHelpPinned]);

  useEffect(() => {
    if (!isOpen || !unitOptions.length || defaultUnitSetRef.current) return;
    defaultUnitSetRef.current = true;
    const defaultUnit =
      defaultUnitFromItem && unitOptions.some((o) => o.value === defaultUnitFromItem)
        ? defaultUnitFromItem
        : unitOptions[0]?.value ?? "Piece";
    setForm((prev) => ({ ...prev, unit: defaultUnit }));
  }, [isOpen, unitOptions, defaultUnitFromItem]);

  const fetchItemDetails = async () => {
    if (!menuItemId || !compID) return;
    try {
      const res = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
        params: { MenuItem_ID: menuItemId, CompID: compID },
      });
      const data = res?.data?.data ?? res?.data ?? null;
      setItemDetails(data);
    } catch (e) {
      setItemDetails(null);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await axiosInstance.get("/api/Unit/SuperAdmin/GetAllUnits", {
        params: { SortBy: "MeasureName", SortOrder: "ASC", PageSize: "1000", PageNumber: 1 },
      });
      const data = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(data) ? data : [];
      setAllUnits(arr);
    } catch (e) {
      setAllUnits([]);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_BY_MENU_ITEM, {
        params: { MenuItem_ID: menuItemId, CompID: compID, IncludeInactive: false },
      });
      const data = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(data) ? data : [];
      setBatches(arr.filter((b) => (b.Quantity ?? b.quantity ?? 0) > 0));
    } catch (e) {
      setBatches([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const qty = parseFloat(form.quantity);
    const isIncrease = form.changeType === "increase";

    if (!qty || qty <= 0) {
      showErrorToast("Please enter a valid quantity");
      return;
    }

    if (isIncrease) {
      const cost = parseFloat(form.costPrice);
      if (cost == null || isNaN(cost) || cost < 0) {
        showErrorToast("Please enter a valid cost price");
        return;
      }
    } else {
      if (form.deductionMode === "batch" && !form.inventoryStockId) {
        showErrorToast("Please select a batch to deduct from");
        return;
      }
    }

    setLoading(true);
    try {
      if (isIncrease) {
        const response = await axiosInstance.post(APIs.POST_ADD_MANUAL_INVENTORY_STOCK, {
          menuItem_ID: menuItemId,
          compID,
          quantity: qty,
          unit: form.unit || "Piece",
          costPrice: parseFloat(form.costPrice),
          batchNumber: form.batchNumber || null,
          expiryDate: form.expiryDate ? new Date(form.expiryDate).toISOString() : null,
          notes: form.notes || null,
        });
        if (response.data?.status) {
          showSuccessToast("Stock increased successfully");
          onSuccess?.();
          onClose();
        } else {
          showErrorToast(response.data?.message || "Failed to increase stock");
        }
      } else {
        const response = await axiosInstance.post(APIs.POST_DEDUCT_MANUAL_INVENTORY_STOCK, {
          menuItem_ID: menuItemId,
          compID,
          quantity: qty,
          unit: form.unit || "Piece",
          inventoryStockId: form.deductionMode === "batch" && form.inventoryStockId ? parseInt(form.inventoryStockId, 10) : null,
          notes: form.notes || null,
        });
        if (response.data?.status) {
          showSuccessToast("Stock decreased successfully");
          onSuccess?.();
          onClose();
        } else {
          showErrorToast(response.data?.message || "Failed to decrease stock");
        }
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || error.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const isIncrease = form.changeType === "increase";
  const itemCostPriceRaw =
    itemDetails?.menuItem_CostPrice ?? itemDetails?.MenuItem_CostPrice ?? null;
  const itemCostPrice =
    itemCostPriceRaw != null && itemCostPriceRaw !== "" && !isNaN(parseFloat(itemCostPriceRaw))
      ? parseFloat(itemCostPriceRaw)
      : null;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Adjust Stock"
      subtitle={itemName ? `Adjust stock for ${itemName}` : "Adjust stock"}
      onSubmit={handleSubmit}
      showButton={true}
      submitButtonText={ "Adjust"}
      submitButtonDisabled={loading}
      modalSize="modal-lg"
      isCentered
      maxHeight="600px"
    >
      <div className="row g-3" style={{ padding: "8px 0" }}>
        <div className="col-12 col-md-6">
          <label className="form-label mb-1" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500 }}>Change</label>
          <Select
            value={CHANGE_OPTIONS.find((o) => o.value === form.changeType) || CHANGE_OPTIONS[0]}
            onChange={(opt) => setForm((prev) => ({ ...prev, changeType: opt?.value || "increase" }))}
            options={CHANGE_OPTIONS}
            styles={dropdownStyles}
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="col-12 col-md-6">
          <FormInput
            label="Quantity"
            placeHolder="0"
            type="number"
            step="0.01"
            min="0.01"
            name="quantity"
            value={form.quantity}
            onchange={(e) => handleChange(e)}
          />
        </div>

        <div className="col-12 col-md-6">
          <label className="form-label mb-1" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500 }}>Unit</label>
          <Select
            value={unitOptions.find((u) => u.value === form.unit) || unitOptions[0] || { value: "Piece", label: "Piece" }}
            onChange={(opt) => setForm((prev) => ({ ...prev, unit: opt?.value || "Piece" }))}
            options={unitOptions}
            styles={dropdownStyles}
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        {isIncrease && (
          <>
            <div className="col-12 col-md-6">
              <div ref={costPriceHelpRef} style={{ position: "relative" }}>
                <label
                  className="form-label mb-1 d-inline-flex align-items-center gap-1"
                  style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500 }}
                >
                  Cost Price
                  <button
                    type="button"
                    aria-label="Cost price info"
                    title="Cost price info"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCostPriceHelpPinned((prev) => {
                        const next = !prev;
                        setShowCostPriceHelp(next);
                        return next;
                      });
                    }}
                    onMouseEnter={() => setShowCostPriceHelp(true)}
                    onMouseLeave={() => {
                      if (!costPriceHelpPinned) setShowCostPriceHelp(false);
                    }}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: themeColor,
                      cursor: "pointer",
                      padding: "0 2px",
                      lineHeight: 1,
                      fontSize: "14px",
                    }}
                  >
                    <i className="fa fa-info-circle" />
                  </button>
                </label>
                <FormInput
                  placeHolder="0.00"
                  type="number"
                  step="0.01"
                  min="0"
                  name="costPrice"
                  value={form.costPrice}
                  onchange={(e) => handleChange(e)}
                />
                {showCostPriceHelp && (
                  <div
                    onMouseEnter={() => setShowCostPriceHelp(true)}
                    onMouseLeave={() => {
                      if (!costPriceHelpPinned) setShowCostPriceHelp(false);
                    }}
                    style={{
                      position: "absolute",
                      zIndex: 20,
                      left: 0,
                      right: 0,
                      top: "100%",
                      marginTop: "4px",
                      padding: "8px 10px",
                      borderRadius: "8px",
                      background: isDarkMode ? "#2d3436" : "#ffffff",
                      border: `1px solid ${themeColor}55`,
                      boxShadow: isDarkMode
                        ? "0 6px 16px rgba(0,0,0,0.45)"
                        : "0 6px 16px rgba(0,0,0,0.12)",
                      fontSize: "12px",
                      lineHeight: 1.45,
                      color: isDarkMode ? "#ced4da" : "#6c757d",
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: "4px", color: isDarkMode ? "#fff" : "#495057" }}>
                      What to enter
                    </div>
                    <div>
                      Enter the <strong>cost per {form.unit || "unit"}</strong> for this stock lot
                      (not the total cost of the whole quantity).
                    </div>
                    <div style={{ marginTop: "4px" }}>
                      Example: if you bought 10 {form.unit || "units"} for 500 total, enter{" "}
                      <strong>50</strong> as cost price.
                    </div>
                    {itemCostPrice != null && (
                      <div style={{ marginTop: "6px", color: themeColor, fontWeight: 500 }}>
                        Current item cost price: {itemCostPrice.toFixed(4)} per unit
                        {" "}
                        <button
                          type="button"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              costPrice: String(Number(itemCostPrice.toFixed(4))),
                            }))
                          }
                          style={{
                            marginLeft: "6px",
                            border: "none",
                            background: "transparent",
                            color: themeColor,
                            textDecoration: "underline",
                            cursor: "pointer",
                            padding: 0,
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          Use this
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="col-12 col-md-6">
              <FormInput
                label="Expiry Date (optional)"
                placeHolder=""
                type="date"
                name="expiryDate"
                value={form.expiryDate}
                onchange={(e) => handleChange(e)}
              />
            </div>
            <div className="col-12 col-md-6">
              <FormInput
                label="Batch Number (optional)"
                placeHolder="Auto-generated if empty"
                type="text"
                name="batchNumber"
                value={form.batchNumber}
                onchange={(e) => handleChange(e)}
              />
            </div>
            <div className="col-12">
              <label className="form-label mb-1" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500 }}>Notes (optional)</label>
              <textarea
                className="form-control"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Optional notes"
                style={{
                  width: "100%",
                  borderRadius: "8px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#fff" : "#000",
                }}
              />
            </div>
          </>
        )}

        {!isIncrease && (
          <>
            <div className="col-12 col-md-6">
              <label className="form-label mb-1" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500 }}>Deduct From</label>
              <Select
                value={
                  form.deductionMode === "batch"
                    ? { value: "batch", label: "Specific batch / lot" }
                    : { value: "fifo", label: "FIFO (automatic - oldest first)" }
                }
                onChange={(opt) => {
                  const val = opt?.value || "fifo";
                  setForm((p) => ({ ...p, deductionMode: val, inventoryStockId: val === "fifo" ? "" : p.inventoryStockId }));
                }}
                options={[
                  { value: "fifo", label: "FIFO (automatic - oldest first)" },
                  { value: "batch", label: "Specific batch / lot" },
                ]}
                styles={dropdownStyles}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            {form.deductionMode === "batch" && (
              <div className="col-12 col-md-6">
                <label className="form-label mb-1" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500 }}>Select Batch</label>
                <Select
                  value={
                    (batches || [])
                      .map((b) => {
                        const id = b.InventoryStockId ?? b.inventoryStockId;
                        const qty = b.Quantity ?? b.quantity ?? 0;
                        const batchNum = b.BatchNumber ?? b.batchNumber ?? "-";
                        const expiry = b.ExpiryDate ?? b.expiryDate;
                        const expiryStr = expiry ? new Date(expiry).toLocaleDateString() : "N/A";
                        return { value: String(id), label: `${batchNum} | Qty: ${qty} | Expiry: ${expiryStr}` };
                      })
                      .find((o) => o.value === String(form.inventoryStockId)) || null
                  }
                  onChange={(opt) => setForm((prev) => ({ ...prev, inventoryStockId: opt?.value || "" }))}
                  options={[
                    ...(batches || []).map((b) => {
                      const id = b.InventoryStockId ?? b.inventoryStockId;
                      const qty = b.Quantity ?? b.quantity ?? 0;
                      const batchNum = b.BatchNumber ?? b.batchNumber ?? "-";
                      const expiry = b.ExpiryDate ?? b.expiryDate;
                      const expiryStr = expiry ? new Date(expiry).toLocaleDateString() : "N/A";
                      return { value: String(id), label: `${batchNum} | Qty: ${qty} | Expiry: ${expiryStr}` };
                    }),
                  ]}
                  styles={dropdownStyles}
                  placeholder="-- Select batch --"
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            )}
            <div className="col-12">
              <label className="form-label mb-1" style={{ color: isDarkMode ? "#fff" : "#000", fontWeight: 500 }}>Notes (optional)</label>
              <textarea
                className="form-control"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Optional notes for this deduction"
                style={{
                  width: "100%",
                  borderRadius: "8px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#fff" : "#000",
                }}
              />
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default AdjustStockModal;
